import { useCallback } from 'react';

import { useKioskSession } from '@/lib/kioskSession';

import type { TranslationKey, TranslationValues } from './dictionary';
import { translate } from './dictionary';

/**
 * Translates a key into the language currently selected on the kiosk.
 *
 * `values` fills `{name}` placeholders, so a sentence carrying data stays one string in the dictionary in
 * both languages rather than being concatenated in a component — see `interpolate` for why that matters
 * more in Spanish than in English.
 */
export type Translate = (key: TranslationKey, values?: TranslationValues) => string;

/**
 * Binds the dictionary to the language the candidate chose on the Welcome screen.
 *
 * It lives in `src/i18n/` rather than in the store because the dictionary is what it is about: components
 * ask for a key and get chrome in the current language, and nothing outside this directory indexes a
 * dictionary directly (`src/i18n/CLAUDE.md`). The language selection itself is cross-screen state, so it
 * comes from the kiosk session store (convention skill § 6).
 */
export function useTranslation(): Translate {
  const language = useKioskSession((state) => state.language);
  return useCallback(
    (key: TranslationKey, values?: TranslationValues) => translate(language, key, values),
    [language],
  );
}
